import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import styles from '@components/drive/Drive.module.css';
import SearchInput, { SearchOptionGroup } from '@components/fieldsNative/SearchInput';
import useFileResourceDownloader from '@hooks/useFileResourceDownloader';
import useMyClient from '@hooks/useMyClient';
import { FileResourceFragment, FolderFragment, GetJourneyWithDocumentsQuery } from '@typing/Generated';
import { getFileResourceTitle, getFolderTitle } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  folder?: FolderFragment;
  journey: GetJourneyWithDocumentsQuery['journey'];
};

const DriveSearch = ({ folder, journey }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const fileResourceDownloader = useFileResourceDownloader();

  const handleDownload = useCallback(
    (fr: FileResourceFragment) => {
      fileResourceDownloader.handleDownload(fr, journey.id);
    },
    [fileResourceDownloader, journey.id]
  );

  const searchOptions = (v: string) => {
    const arr: SearchOptionGroup[] = [];
    const files = journey.fileResources
      .filter(
        file =>
          getFileResourceTitle(file, intl).toLowerCase().includes(v.toLowerCase()) && (file.attachment || file.pdfType)
      )
      .filter(file => !folder || file.folder?.id === folder.id);

    if (files.length > 0) {
      arr.push({
        label: intl.formatMessage({ id: 'pages.drive.files' }),
        options: files.map(file => ({
          category: intl.formatMessage({ id: 'pages.drive.files' }),
          label: getFileResourceTitle(file, intl),
          onClick: () => handleDownload(file),
          value: file.id
        }))
      });
    }
    const folders = journey.folders.filter(folder =>
      getFolderTitle(folder, intl).toLowerCase().includes(v.toLowerCase())
    );
    if (folders.length > 0 && !folder) {
      arr.push({
        label: intl.formatMessage({ id: 'pages.drive.folders' }),
        options: folders.map(folder => ({
          category: intl.formatMessage({ id: 'pages.drive.folders' }),
          label: getFolderTitle(folder, intl),
          link: journeyRoute({ journeyId: journey.id, linkAfterJourney: `/drive/folders/${folder.id}`, myClient }),
          value: folder.id
        }))
      });
    }
    const worksheets = journey.fileResources
      .filter(file => file.worksheet && file.title?.toLowerCase().includes(v.toLowerCase()))
      .filter(file => !folder || file.folder?.id === folder.id);
    if (worksheets.length > 0) {
      arr.push({
        label: intl.formatMessage({ id: 'pages.drive.worksheets' }),
        options: worksheets.map(file => ({
          category: intl.formatMessage({ id: 'pages.drive.worksheets' }),
          label: file.title ?? '',
          link: journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: `/worksheets/${file.worksheet?.id ?? ''}`,
            myClient
          }),
          value: file.id
        }))
      });
    }

    return Promise.resolve(arr);
  };

  return (
    <div className={styles.fullWidth}>
      <SearchInput
        error=""
        initialValue=""
        name="driveSearch"
        noSpacing
        onSearch={searchOptions}
        placeholder={intl.formatMessage({ id: 'pages.drive.searchDrive' })}
        setValue={() => {}}
      />
    </div>
  );
};

export default DriveSearch;
